import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffAttApi, errMsg, todayIST } from '@/services/staffAttendanceApi';

export const SectionInput = ({ value, onChange, sections, testId, placeholder = 'Section' }) => (
  <>
    <Input value={value} onChange={(e) => onChange(e.target.value)} list={`${testId}-list`} placeholder={placeholder} className="bg-white" data-testid={testId} />
    <datalist id={`${testId}-list`}>{sections.map((s) => <option key={s} value={s} />)}</datalist>
  </>
);

const Shell = ({ open, onClose, title, desc, testId, busy, onSave, saveLabel, children }) => (
  <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
    <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={testId}>
      <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
      <div className="space-y-3">{children}</div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button onClick={onSave} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid={`${testId}-save`}>{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{saveLabel}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const useRun = (fn, onDone, ok) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await fn(); toast.success(ok); onDone(); } catch (e) { toast.error(errMsg(e, 'Failed')); } finally { setBusy(false); }
  };
  return [busy, run];
};

export const RaiseRequestDialog = ({ open, onClose, onDone, sections, date }) => {
  const [f, setF] = useState({ section: '', men: 0, women: 0, note: '' });
  useEffect(() => { if (open) setF({ section: '', men: 0, women: 0, note: '' }); }, [open]);
  const [busy, run] = useRun(() => staffAttApi.raiseRequest({ ...f, men: Number(f.men), women: Number(f.women), date }), onDone, 'Request raised — all supervisors notified');
  return (
    <Shell open={open} onClose={onClose} title="Request manpower" desc={`For ${date}. Every supervisor sees it and can send free staff.`} testId="mp-raise-dialog" busy={busy} onSave={run} saveLabel="Raise request">
      <div><Label className="text-xs">Your section</Label><SectionInput value={f.section} onChange={(v) => setF({ ...f, section: v })} sections={sections} testId="mp-raise-section" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Men</Label><Input type="number" min={0} value={f.men} onChange={(e) => setF({ ...f, men: e.target.value })} data-testid="mp-raise-men" /></div>
        <div><Label className="text-xs">Women</Label><Input type="number" min={0} value={f.women} onChange={(e) => setF({ ...f, women: e.target.value })} data-testid="mp-raise-women" /></div>
      </div>
      <div><Label className="text-xs">Note</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. urgent packing order" data-testid="mp-raise-note" /></div>
    </Shell>
  );
};

/** Pick free staff from my sections (HR: + Contract Pool) — send against a request, or deploy directly. */
export const StaffPickerDialog = ({ open, onClose, onDone, date, request, sections }) => {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState([]);
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!open) return;
    setSel([]); setTo(''); setQ(''); setRows(null);
    staffAttApi.sendable(date).then(setRows).catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [open, date]);
  const list = useMemo(() => (rows || []).filter((r) => !q || `${r.staff_id} ${r.staff_name} ${r.section}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const picked = (rows || []).filter((r) => sel.includes(r.staff_id));
  const men = picked.filter((r) => r.gender === 'male').length;
  const women = picked.filter((r) => r.gender === 'female').length;
  const [busy, run] = useRun(() => (request ? staffAttApi.sendToRequest(request.request_id, sel) : staffAttApi.deployDirect({ staff_ids: sel, to_section: to, date })),
    onDone, 'Sent — waiting for the receiving supervisor to accept');
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <Shell open={open} onClose={onClose} testId="mp-picker-dialog" busy={busy} onSave={run} saveLabel={`Send ${sel.length} staff`}
      title={request ? `Send staff to ${request.section}` : 'Deploy staff for the day'}
      desc={request ? `Still open: ${request.open_men} men, ${request.open_women} women. Selected: ${men} men, ${women} women.` : `For ${date}. The receiving supervisor accepts each person on arrival.`}>
      {!request && <div><Label className="text-xs">To section</Label><SectionInput value={to} onChange={setTo} sections={sections} testId="mp-picker-to" /></div>}
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" data-testid="mp-picker-search" />
      {!rows ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
        <div className="max-h-72 overflow-y-auto divide-y border rounded-lg">
          {list.length === 0 && <p className="p-4 text-sm text-gray-500 text-center" data-testid="mp-picker-empty">No staff you can send.</p>}
          {list.map((r) => (
            <label key={r.staff_id} className={`flex items-center gap-2 px-3 py-2 text-sm ${r.busy_with ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'}`} data-testid={`mp-pick-${r.staff_id}`}>
              <input type="checkbox" disabled={!!r.busy_with} checked={sel.includes(r.staff_id)} onChange={() => toggle(r.staff_id)} data-testid={`mp-pick-check-${r.staff_id}`} />
              <span className="flex-1"><b>{r.staff_name}</b> <span className="text-gray-500">{r.staff_id} · {r.section}</span></span>
              <span className="text-xs text-gray-500">{r.gender === 'male' ? 'M' : r.gender === 'female' ? 'W' : '?'}</span>
              {r.checked_in && <span className="text-[11px] rounded-full bg-emerald-100 text-emerald-700 px-2">in today</span>}
              {r.busy_with && <span className="text-[11px] text-gray-500">{r.busy_with}</span>}
            </label>
          ))}
        </div>
      )}
    </Shell>
  );
};

export const TransferDialog = ({ open, onClose, onDone, staff, sections }) => {
  const [f, setF] = useState({ to_section: '', effective_date: todayIST(), reason: '' });
  useEffect(() => { if (open) setF({ to_section: '', effective_date: todayIST(), reason: '' }); }, [open]);
  const [busy, run] = useRun(() => staffAttApi.transferStaff(staff.staff_id, f), onDone, 'Transferred — new section supervisor notified');
  return (
    <Shell open={open && !!staff} onClose={onClose} title={`Transfer ${staff?.staff_name || ''}`} desc={`Permanent move from ${staff?.section || '—'}. Logged in Edit History.`}
      testId="transfer-dialog" busy={busy} onSave={run} saveLabel="Transfer">
      <div><Label className="text-xs">New section</Label><SectionInput value={f.to_section} onChange={(v) => setF({ ...f, to_section: v })} sections={sections} testId="transfer-to" /></div>
      <div><Label className="text-xs">Effective date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="transfer-date" /></div>
      <div><Label className="text-xs">Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="transfer-reason" /></div>
    </Shell>
  );
};

export const AgencyDialog = ({ open, onClose, onDone, request }) => {
  const [f, setF] = useState({ men: 0, women: 0, agency: '' });
  useEffect(() => { if (open) setF({ men: 0, women: 0, agency: '' }); }, [open]);
  const [busy, run] = useRun(() => staffAttApi.agencySupply(request.request_id, { ...f, men: Number(f.men), women: Number(f.women) }), onDone, 'Agency manpower recorded');
  return (
    <Shell open={open && !!request} onClose={onClose} title="Record agency manpower" desc={`Balance: ${request?.balance_men} men, ${request?.balance_women} women.`} testId="agency-dialog" busy={busy} onSave={run} saveLabel="Save">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Men</Label><Input type="number" min={0} value={f.men} onChange={(e) => setF({ ...f, men: e.target.value })} data-testid="agency-men" /></div>
        <div><Label className="text-xs">Women</Label><Input type="number" min={0} value={f.women} onChange={(e) => setF({ ...f, women: e.target.value })} data-testid="agency-women" /></div>
      </div>
      <div><Label className="text-xs">Agency name</Label><Input value={f.agency} onChange={(e) => setF({ ...f, agency: e.target.value })} data-testid="agency-name" /></div>
    </Shell>
  );
};
